// ─── Project Map — shared config + persistence helpers ───────────────────────
// Per-project map configuration: pin colours, base tile layer, default
// centre + zoom. Persisted in localStorage keyed by project id, matching the
// project-scoped storage convention used elsewhere in the dashboard
// (`wq_<kind>_p<projectId>`). Account-level provider settings (Google /
// Esri BYO key) are a Phase 2 concern and intentionally NOT modelled here —
// this file only handles per-project map layout + pin styling that the
// "Map Editor" popup writes to.
// ─────────────────────────────────────────────────────────────────────────────

// Storage key prefix — keep in step with the convention in DashboardWidgets.jsx.
const STORAGE_PREFIX = 'wq_mapcfg_p'

// Site-type taxonomy. The XD "Pin Settings" section lists three buckets:
// Land Based Station / Pond Buoy / Weather Station. The legacy Symfony
// data model doesn't carry a strict enum for this, so the React side bins
// sites by a best-effort heuristic on site.description / site.site_code /
// site.name keywords, falling back to "land". Customers can override the
// inferred bucket per-site in Phase 2 (out of scope for this commit).
export const SITE_TYPES = [
    { key: 'land',    label: 'Land Based Station', defaultColor: '#8B5CF6' }, // purple
    { key: 'buoy',    label: 'Pond Buoy',          defaultColor: '#F59E0B' }, // amber
    { key: 'weather', label: 'Weather Station',    defaultColor: '#10B981' }, // emerald
]

// Tile layers — all free, no API key required. The "Imagery" option uses
// Esri World Imagery's free tile service (no key, ToS allows attribution-only
// usage); "Topographic" uses OpenTopoMap. Customers who want Google Maps
// satellite tiles will be served via the Phase 2 BYO-key flow.
export const TILE_LAYERS = [
    {
        key: 'streets',
        label: 'Streets',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
    },
    {
        key: 'imagery',
        label: 'Imagery',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
        maxZoom: 19,
    },
    {
        key: 'topographic',
        label: 'Topographic',
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
        maxZoom: 17,
    },
]

export function getTileLayer(key) {
    return TILE_LAYERS.find((l) => l.key === key) || TILE_LAYERS[0]
}

// Heuristic: pick a site-type bucket from a site's free-text fields. This
// keeps the XD pin-colour groups working without a schema change. If a
// customer needs deterministic categorisation they'll set it explicitly
// in Phase 2.
export function inferSiteType(site) {
    const blob = [
        site?.name,
        site?.description,
        site?.site_code,
    ].filter(Boolean).join(' ').toLowerCase()
    if (/buoy|pond|float|raft/.test(blob))                return 'buoy'
    if (/weather|met[\s-]?station|wx|anemom|atmospher/.test(blob)) return 'weather'
    return 'land'
}

// Default config — used the first time a project's Map Editor is opened.
export function defaultMapConfig() {
    const pinColors = {}
    SITE_TYPES.forEach((t) => { pinColors[t.key] = t.defaultColor })
    return {
        pinColors,
        tileLayer: 'imagery',       // matches the XD which shows "Imagery"
        centerLat: null,            // null → fit-to-markers on first render
        centerLng: null,
        zoom: 8,
    }
}

export function loadMapConfig(projectId) {
    if (projectId == null) return defaultMapConfig()
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${projectId}`)
        if (!raw) return defaultMapConfig()
        const parsed = JSON.parse(raw)
        // Shallow merge over defaults so newly-added config keys don't break
        // existing saved configs.
        const base = defaultMapConfig()
        return {
            ...base,
            ...parsed,
            pinColors: { ...base.pinColors, ...(parsed?.pinColors || {}) },
        }
    } catch {
        return defaultMapConfig()
    }
}

export function saveMapConfig(projectId, cfg) {
    if (projectId == null) return
    try {
        localStorage.setItem(`${STORAGE_PREFIX}${projectId}`, JSON.stringify(cfg))
    } catch {
        /* quota exceeded — ignore, the config will simply not persist */
    }
}
